// Settings → Account password / delete-account / sessions-revoke
// glue. Form modals (password + delete) post to a single endpoint;
// sessions card buttons POST per-row revoke or revoke-others and
// reload the page on success.

import { showToast } from "./toast.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const FORM_MODAL_SELECTOR = "[data-account-form-modal]";
const SESSIONS_SELECTOR = "[data-account-sessions]";

export function initAccountActions(root = document) {
  root.querySelectorAll(FORM_MODAL_SELECTOR).forEach(wireFormModal);
  root.querySelectorAll(SESSIONS_SELECTOR).forEach(wireSessions);
}

function wireFormModal(modal) {
  if (!firstBind(modal)) return;

  const actionURL = modal.dataset.actionUrl;
  const successRedirect = modal.dataset.successRedirect;
  if (!actionURL) return;

  const form = modal.querySelector("form");
  if (!form) return;

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearFieldErrors(modal);

    const params = formAsURLSearchParams(form);

    try {
      const resp = await fetch(actionURL, {
        method: "POST",
        body: params,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (resp.ok) {
        if (successRedirect) {
          window.location.assign(successRedirect);
          return;
        }
        modal.classList.add("hidden");
        window.location.reload();
        return;
      }

      const body = await safeJSON(resp);
      if (
        body &&
        body.field === "_workspaces" &&
        Array.isArray(body.workspaces)
      ) {
        renderBlockingWorkspaces(modal, body);
      } else if (body && body.field) {
        surfaceFieldError(
          modal,
          body.field,
          body.error || "Something went wrong.",
        );
      } else {
        surfaceFieldError(
          modal,
          "_form",
          (body && body.error) || `Request failed (${resp.status})`,
        );
      }
    } catch (err) {
      surfaceFieldError(modal, "_form", err.message || "Network error.");
    }
  });

  // Cancel + backdrop close handling lives in modal_close.js as a
  // document-level delegated listener.
}

function wireSessions(card) {
  if (!firstBind(card)) return;

  card.addEventListener("click", async (event) => {
    const revokeBtn = event.target.closest("[data-account-session-revoke]");
    const revokeOthersBtn = event.target.closest(
      "[data-account-sessions-revoke-others]",
    );

    if (revokeBtn) {
      const sessionID = revokeBtn.dataset.accountSessionRevoke;
      revokeBtn.disabled = true;
      try {
        const resp = await fetch(
          `/settings/account/sessions/${encodeURIComponent(sessionID)}/revoke`,
          {
            method: "POST",
            headers: { Accept: "application/json" },
            credentials: "same-origin",
          },
        );
        if (!resp.ok) throw new Error(`Revoke failed (${resp.status})`);
        const row = card.querySelector(
          `[data-account-session-id="${cssEscape(sessionID)}"]`,
        );
        if (row) row.remove();
      } catch (err) {
        revokeBtn.disabled = false;
        revokeBtn.textContent = "Failed";
      }
      return;
    }

    if (revokeOthersBtn) {
      revokeOthersBtn.disabled = true;
      try {
        const resp = await fetch("/settings/account/sessions/revoke-others", {
          method: "POST",
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (!resp.ok) throw new Error(`Request failed (${resp.status})`);

        let n = 0;
        card.querySelectorAll("[data-account-session-id]").forEach((row) => {
          if (!row.querySelector("[data-account-session-revoke]")) return;
          row.remove();
          n++;
        });
        revokeOthersBtn.replaceWith(disabledRevokeOthersBtn());
        showToast(
          n === 0
            ? "No other sessions to sign out."
            : `Signed out ${n} other ${n === 1 ? "session" : "sessions"}.`,
          n === 0 ? "info" : "success",
        );
      } catch (err) {
        revokeOthersBtn.disabled = false;
        revokeOthersBtn.textContent = "Failed — try again";
      }
    }
  });
}

// Mirrors the disabled branch in account.templ; if either changes,
// keep them in sync.
function disabledRevokeOthersBtn() {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.disabled = true;
  btn.title = "No other sessions to sign out.";
  btn.className =
    "px-4 py-2 rounded-xl text-sm font-medium text-ink-500 cursor-not-allowed opacity-70";
  btn.textContent = "Sign out all other sessions";

  return btn;
}

function formAsURLSearchParams(form) {
  const params = new URLSearchParams();
  const data = new FormData(form);
  for (const [k, v] of data.entries()) {
    if (typeof v === "string") params.append(k, v);
  }
  return params;
}

async function safeJSON(resp) {
  try {
    return await resp.json();
  } catch (_) {
    return null;
  }
}

function clearFieldErrors(scope) {
  scope.querySelectorAll("[data-field-error]").forEach((el) => {
    el.textContent = "";
    el.classList.add("hidden");
  });
}

function surfaceFieldError(scope, field, message) {
  let target = scope.querySelector(`[data-field-error='${cssEscape(field)}']`);
  if (!target) target = scope.querySelector("[data-field-error='_form']");
  if (!target) return;
  target.textContent = message;
  target.classList.remove("hidden");
}

function renderBlockingWorkspaces(scope, body) {
  const target = scope.querySelector("[data-field-error='_workspaces']");
  if (!target) return;
  target.innerHTML = "";

  const intro = document.createElement("p");
  intro.className = "font-medium";
  intro.textContent =
    body.error || "Resolve workspace ownership before deleting.";
  target.appendChild(intro);

  const list = document.createElement("ul");
  list.className = "list-disc pl-5";
  for (const ws of body.workspaces || []) {
    const li = document.createElement("li");
    li.textContent = ws.name || ws.workspace_id;
    list.appendChild(li);
  }
  target.appendChild(list);
  target.classList.remove("hidden");
}

// cssEscape mirrors CSS.escape with a small ASCII-safe fallback for
// older browsers. Session IDs are UUIDs so the fallback is enough.
function cssEscape(str) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(str);
  }
  return String(str).replace(/[^a-zA-Z0-9_\-]/g, "\\$&");
}
